import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
  Button,
  Grid2 as Grid,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import Checkbox from "@mui/material/Checkbox";

import { RootState } from "@app/store";
import LoadingIndicatorBox from "@features/common/LoadingIndicatorBox/LoadingIndicatorBox";
import CloudPlatformChip from "@features/common/CloudPlatformChip";
import { selectVMStatus } from "@features/environments/environmentsSlice";
import { IVirtualMachine } from "@features/environments/types";
import { getSelectedTask } from "../tasksSlice";
import { ITask } from "../types";

interface LeftRightProps {
  vmCategory: string;
  virtualmachines: IVirtualMachine[];
  setleftGlobal: React.Dispatch<React.SetStateAction<string[]>>;
  isEditing: boolean;
}

export const LeftRightSelector = (props: LeftRightProps) => {
  const { t } = useTranslation();

  const { vmCategory, virtualmachines, setleftGlobal, isEditing } = props;

  const [leftList, setLeftListLocal] = useState<string[]>([]);
  const [rightList, setRightListLocal] = useState<string[]>([]);
  const [checked, setChecked] = useState<string[]>([]);
  const vmListLoading = useSelector(selectVMStatus) === "loading";

  const leftChecked = leftList.filter((value) => checked.includes(value));
  const rightChecked = rightList.filter((value) => checked.includes(value));

  const selectedTask: ITask | undefined = useSelector((state: RootState) =>
    getSelectedTask(state),
  );

  // Populate left and right lists.
  // Left list contains VMs that are already selected for the task (selectedTask.vm_names)
  // Right list contains the rest of the VMs
  useEffect(() => {
    if (virtualmachines && selectedTask) {
      const vmNamesLeft: string[] = virtualmachines.reduce(
        (acc: string[], vm: IVirtualMachine) => {
          if (
            vm.category === vmCategory &&
            selectedTask.vm_names?.includes(vm.name)
          ) {
            return [...acc, vm.name];
          }
          return acc;
        },
        [],
      );

      const vmNamesRight: string[] = virtualmachines.reduce(
        (acc: string[], vm: IVirtualMachine) => {
          if (
            vm.category === vmCategory &&
            !selectedTask.vm_names?.includes(vm.name)
          ) {
            return [...acc, vm.name];
          }
          return acc;
        },
        [],
      );

      setRightListLocal(vmNamesRight);
      setLeftListLocal(vmNamesLeft);
    }
    if (!isEditing) {
      setChecked([]);
    }
  }, [virtualmachines, isEditing]);

  const handleSelectedVM = (value: any) => {
    // Disables selection if not in edit mode, but preserves styles and selection highlight for better UX
    if (!isEditing) return;
    const selected = [...checked];
    if (selected.includes(value)) {
      selected.splice(selected.indexOf(value), 1);
    } else {
      selected.push(value);
    }
    setChecked(selected);
  };

  // moves vm from right to left view
  // currently de-selects item after
  const shiftVMLeft = () => {
    if (checked) {
      setLeftListLocal(leftList.concat(rightChecked));
      setRightListLocal(rightList.filter((str) => !rightChecked.includes(str)));
      setChecked(checked.filter((str) => !rightChecked.includes(str)));
      setleftGlobal((prev) => [
        ...prev,
        ...rightChecked.filter((str) => !prev.includes(str)),
      ]);
    }
  };

  // moves vm from left to right view
  // currently de-selects item after
  const shiftVMRight = () => {
    if (checked) {
      setRightListLocal(rightList.concat(leftChecked));
      setLeftListLocal(leftList.filter((str) => !leftChecked.includes(str)));
      setChecked(checked.filter((str) => !leftChecked.includes(str)));
      setleftGlobal((prev) => prev.filter((str) => !leftChecked.includes(str)));
    }
  };

  const selectorList = (itemList: string[], selectorId: string) => (
    <Paper
      sx={{ height: 230, overflow: "auto", marginBottom: { sm: 2.5, xs: 1 } }}
    >
      <List dense component="div" role="list" data-testid={selectorId}>
        {itemList.map((value: any) => {
          const labelId = `transfer-list-item-${value}-label`;
          return (
            <ListItemButton
              key={value}
              role="listitem"
              onClick={() => handleSelectedVM(value)}
              disableRipple={!isEditing}
            >
              <ListItemIcon>
                <Checkbox
                  checked={checked.includes(value)}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{
                    "aria-labelledby": labelId,
                  }}
                  sx={{ paddingLeft: 0.5 }}
                  disabled={!isEditing}
                />
              </ListItemIcon>
              <ListItemText id={labelId} primary={value} />
              <ListItemIcon>
                {vmPlatformChip(
                  virtualmachines.find((vm) => vm.name === value)
                    ?.cloud_platform,
                )}
              </ListItemIcon>
            </ListItemButton>
          );
        })}
      </List>
    </Paper>
  );

  const vmPlatformChip = (cloudPlatform: string | null | undefined) => (
    <CloudPlatformChip cloudPlatform={cloudPlatform} />
  );

  return (
    <>
      <Grid
        container
        justifyContent={"center"}
        alignContent={"center"}
        spacing={0}
      >
        <Grid justifyContent={"center"} size={{ xs: 12, sm: 5 }}>
          {!vmListLoading && (
            <>
              <Typography variant="subtitle1" align="center">
                {t("label-split-selected")}
              </Typography>
              {selectorList(leftList, "vms-left")}
            </>
          )}
          <LoadingIndicatorBox show={vmListLoading} />
        </Grid>

        <Grid
          size={{ xs: 12, sm: 2 }}
          container
          justifyContent="center"
          direction="column"
          alignContent="center"
        >
          <Grid>
            <Button
              variant="contained"
              data-testid="right-arrow"
              disabled={leftChecked.length === 0 || !isEditing}
              onClick={shiftVMRight}
              style={{ margin: "4px" }}
            >
              <ChevronRight />
            </Button>
          </Grid>

          <Grid>
            <Button
              variant="contained"
              data-testid="left-arrow"
              disabled={rightChecked.length === 0 || !isEditing}
              onClick={shiftVMLeft}
              style={{ margin: "4px" }}
            >
              <ChevronLeft />
            </Button>
          </Grid>
        </Grid>

        <Grid justifyContent={"center"} size={{ xs: 12, sm: 5 }}>
          {!vmListLoading && (
            <>
              <Typography variant="subtitle1" align="center">
                {t("label-split-available")}
              </Typography>
              {selectorList(rightList, "vms-right")}
            </>
          )}
          <LoadingIndicatorBox show={vmListLoading} />
        </Grid>
      </Grid>
    </>
  );
};
